import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  adminAutomationService, 
  AutomationWithUser, 
  ExecutionWithDetails,
  SystemAutomationStats 
} from '../../services/adminAutomationService';
import AdminEcommerceEventTester from './AdminEcommerceEventTester';
import AdminSampleAutomationCreator from './AdminSampleAutomationCreator';
import ConfirmModal from '../ConfirmModal';

type TabType = 'overview' | 'automations' | 'executions' | 'analytics';

const AdminAutomationControl: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { showSuccess, showError } = useNotification();
  
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SystemAutomationStats | null>(null);
  const [automations, setAutomations] = useState<AutomationWithUser[]>([]);
  const [executions, setExecutions] = useState<ExecutionWithDetails[]>([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAutomations, setSelectedAutomations] = useState<string[]>([]);
  
  // Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type: 'delete' | 'status' | 'cancel' | 'bulk';
    isLoading?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'status'
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load stats for overview
      if (activeTab === 'overview' || activeTab === 'analytics') {
        const systemStats = await adminAutomationService.getSystemStats();
        setStats(systemStats);
      }
      
      // Load automations
      if (activeTab === 'automations' || activeTab === 'overview') {
        const allAutomations = await adminAutomationService.getAllAutomations({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: activeTab === 'overview' ? 10 : undefined
        });
        setAutomations(allAutomations);
      }
      
      // Load executions
      if (activeTab === 'executions') {
        const allExecutions = await adminAutomationService.getAllExecutions({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: 50
        });
        setExecutions(allExecutions);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load automation data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateStatus = (automationId: string, automationName: string, newStatus: 'active' | 'paused' | 'archived') => {
    setConfirmModal({
      isOpen: true,
      title: `Confirm Status Change`,
      message: `Are you sure you want to ${newStatus} the automation "${automationName}"?`,
      confirmText: newStatus.charAt(0).toUpperCase() + newStatus.slice(1),
      type: 'status',
      onConfirm: async () => {
        try {
          setConfirmModal(prev => ({ ...prev, isLoading: true }));
          await adminAutomationService.forceUpdateAutomationStatus(automationId, newStatus);
          showSuccess(`Automation ${newStatus} successfully`);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          loadData();
        } catch (error) {
          console.error('Error updating automation:', error);
          showError('Failed to update automation');
        } finally {
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };
  
  const handleDelete = (automationId: string, automationName: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Automation',
      message: `Are you sure you want to delete "${automationName}"? This action cannot be undone and will delete all associated executions.`,
      confirmText: 'Delete',
      type: 'delete',
      onConfirm: async () => {
        try {
          setConfirmModal(prev => ({ ...prev, isLoading: true }));
          await adminAutomationService.forceDeleteAutomation(automationId);
          showSuccess('Automation deleted successfully');
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          loadData();
        } catch (error) {
          console.error('Error deleting automation:', error);
          showError('Failed to delete automation');
        } finally {
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };
  
  const handleCancelExecution = (executionId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancel Execution',
      message: 'Are you sure you want to cancel this execution?',
      confirmText: 'Cancel Execution',
      type: 'cancel',
      onConfirm: async () => {
        try {
          setConfirmModal(prev => ({ ...prev, isLoading: true }));
          await adminAutomationService.cancelExecution(executionId);
          showSuccess('Execution cancelled successfully');
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          loadData();
        } catch (error) {
          console.error('Error cancelling execution:', error);
          showError('Failed to cancel execution');
        } finally {
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };
  
  const handleBulkAction = (action: 'pause' | 'activate' | 'archive' | 'delete') => {
    if (selectedAutomations.length === 0) {
      showError('Please select at least one automation');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Bulk Action',
      message: `Are you sure you want to ${action} ${selectedAutomations.length} automation(s)?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      type: 'bulk',
      onConfirm: async () => {
        try {
          setConfirmModal(prev => ({ ...prev, isLoading: true }));
          if (action === 'delete') {
            await adminAutomationService.bulkDelete(selectedAutomations);
          } else {
            const statusMap = {
              'pause': 'paused' as const,
              'activate': 'active' as const,
              'archive': 'archived' as const
            };
            await adminAutomationService.bulkUpdateStatus(selectedAutomations, statusMap[action]);
          }
          
          showSuccess(`Bulk ${action} completed successfully`);
          setSelectedAutomations([]);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          loadData();
        } catch (error) {
          console.error(`Error performing bulk ${action}:`, error);
          showError(`Failed to ${action} automations`);
        } finally {
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };
  
  const toggleSelection = (automationId: string) => {
    setSelectedAutomations(prev => 
      prev.includes(automationId)
        ? prev.filter(id => id !== automationId)
        : [...prev, automationId]
    );
  };
  
  const toggleSelectAll = () => {
    if (selectedAutomations.length === filteredAutomations.length) {
      setSelectedAutomations([]);
    } else {
      setSelectedAutomations(filteredAutomations.map(a => a.id!));
    }
  };
  
  // Filter automations
  const filteredAutomations = automations.filter(automation => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matches = 
        automation.name.toLowerCase().includes(searchLower) ||
        automation.description?.toLowerCase().includes(searchLower) ||
        automation.userEmail?.toLowerCase().includes(searchLower) ||
        automation.userName?.toLowerCase().includes(searchLower);
      return matches;
    }
    return true;
  });
  
  // Paginate automations
  const totalPages = Math.ceil(filteredAutomations.length / itemsPerPage);
  const paginatedAutomations = filteredAutomations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'archived':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'cancelled':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };
  
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  if (loading && !stats) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading automation control panel...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'} p-8`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Automation Control Panel
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Monitor and manage all automations across the platform
          </p>
        </div>
        
        {/* Tabs */}
        <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} mb-6`}>
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview' as const, label: '📊 Overview', count: null },
              { id: 'automations' as const, label: '🔄 Automations', count: automations.length },
              { id: 'executions' as const, label: '▶️ Executions', count: executions.length },
              { id: 'analytics' as const, label: '📈 Analytics', count: null }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? isDark 
                      ? 'border-purple-500 text-purple-400' 
                      : 'border-purple-600 text-purple-700'
                    : isDark
                      ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== null && ` (${tab.count})`}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Quick create a permissive sample automation */}
            <AdminSampleAutomationCreator />
            {/* Admin test tool: insert e-commerce event */}
            <AdminEcommerceEventTester />
            {/* System Statistics */}
            <div>
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                System Statistics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} ${isDark ? '' : 'border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md hover:shadow-lg'} p-6 transition-shadow`}>
                  <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Total Automations</div>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{stats.totalAutomations}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
                    {stats.activeAutomations} active • {stats.pausedAutomations} paused
                  </div>
                </div>
                
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} ${isDark ? '' : 'border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md hover:shadow-lg'} p-6 transition-shadow`}>
                  <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Total Executions</div>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{stats.totalExecutions}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
                    {stats.runningExecutions} running • {stats.failedExecutions} failed
                  </div>
                </div>
                
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} ${isDark ? '' : 'border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md hover:shadow-lg'} p-6 transition-shadow`}>
                  <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Success Rate</div>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
                    {stats.averageCompletionRate.toFixed(1)}%
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
                    Average across all automations
                  </div>
                </div>
                
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} ${isDark ? '' : 'border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md hover:shadow-lg'} p-6 transition-shadow`}>
                  <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Emails Sent</div>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
                    {stats.totalEmailsSent.toLocaleString()}
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
                    {stats.totalSmsSent} SMS • {stats.totalPushSent} Push
                  </div>
                </div>
              </div>
            </div>
            
            {/* Top Performing Automations */}
            <div>
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Top Performing Automations
              </h2>
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} ${isDark ? '' : 'border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} overflow-hidden`}>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Automation
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Success Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {stats.topPerformingAutomations.length > 0 ? (
                      stats.topPerformingAutomations.map((automation, index) => (
                        <tr key={automation.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`px-6 py-4 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <div className="flex items-center">
                              <span className={`mr-2 font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>#{index + 1}</span>
                              {automation.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full" 
                                  style={{ width: `${automation.successRate}%` }}
                                ></div>
                              </div>
                              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {automation.successRate.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className={`px-6 py-4 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Recent Failures */}
            {stats.recentFailures.length > 0 && (
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Recent Failures
                </h2>
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} ${isDark ? '' : 'border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} overflow-hidden`}>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {stats.recentFailures.slice(0, 5).map((failure) => (
                      <div key={failure.id} className={`p-4 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className={`text-sm font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                              {failure.error}
                            </div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                              {formatDate(failure.timestamp)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Automations Tab */}
        {activeTab === 'automations' && (
          <div className="space-y-6">
            {/* Filters and Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                placeholder="Search automations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`flex-1 px-4 py-2.5 rounded-lg border transition-colors ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-purple-500'
                } focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-0`}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-purple-500`}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
              <button
                onClick={() => loadData()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                🔄 Refresh
              </button>
            </div>
            
            {/* Bulk Actions */}
            {selectedAutomations.length > 0 && (
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} p-4`}>
                <div className="flex items-center justify-between">
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    {selectedAutomations.length} automation(s) selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleBulkAction('activate')}
                      className={`px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors ${isDark ? '' : 'shadow-sm hover:shadow'}`}
                    >
                      ▶️ Activate
                    </button>
                    <button
                      onClick={() => handleBulkAction('pause')}
                      className={`px-3 py-1.5 text-sm font-medium bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors ${isDark ? '' : 'shadow-sm hover:shadow'}`}
                    >
                      ⏸️ Pause
                    </button>
                    <button
                      onClick={() => handleBulkAction('archive')}
                      className={`px-3 py-1.5 text-sm font-medium bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors ${isDark ? '' : 'shadow-sm hover:shadow'}`}
                    >
                      📦 Archive
                    </button>
                    <button
                      onClick={() => handleBulkAction('delete')}
                      className={`px-3 py-1.5 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors ${isDark ? '' : 'shadow-sm hover:shadow'}`}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Automations Table */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedAutomations.length === filteredAutomations.length && filteredAutomations.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Automation
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        User
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Status
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Stats
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Updated
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {paginatedAutomations.length > 0 ? (
                      paginatedAutomations.map((automation) => (
                        <tr key={automation.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedAutomations.includes(automation.id!)}
                              onChange={() => toggleSelection(automation.id!)}
                              className="rounded"
                            />
                          </td>
                          <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <div className="font-medium">{automation.name}</div>
                            {automation.description && (
                              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate max-w-xs`}>
                                {automation.description}
                              </div>
                            )}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <div className="text-sm">{automation.userName || 'Unknown'}</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {automation.userEmail}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(automation.status)}`}>
                              {automation.status}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <div>{automation.statistics.totalRuns || 0} runs</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {automation.statistics.emailsSent || 0} emails
                            </div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatDate(automation.updatedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              {automation.status === 'active' ? (
                                <button
                                  onClick={() => handleUpdateStatus(automation.id!, automation.name, 'paused')}
                                  className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                                  title="Pause"
                                >
                                  ⏸️
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUpdateStatus(automation.id!, automation.name, 'active')}
                                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                  title="Activate"
                                >
                                  ▶️
                                </button>
                              )}
                              <button
                                onClick={() => handleUpdateStatus(automation.id!, automation.name, 'archived')}
                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                title="Archive"
                              >
                                📦
                              </button>
                              <button
                                onClick={() => handleDelete(automation.id!, automation.name)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className={`px-6 py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div className="text-5xl mb-4">🔍</div>
                          <div>No automations found</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAutomations.length)} of {filteredAutomations.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      currentPage === 1
                        ? isDark
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : isDark
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm hover:shadow'
                    }`}
                  >
                    Previous
                  </button>
                  <span className={`px-3 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      currentPage === totalPages
                        ? isDark
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : isDark
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm hover:shadow'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Executions Tab */}
        {activeTab === 'executions' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-purple-500`}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={() => loadData()}
                className={`px-4 py-2.5 ${isDark ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-600 hover:bg-purple-700 shadow-sm hover:shadow'} text-white rounded-lg transition-all font-medium`}
              >
                🔄 Refresh
              </button>
            </div>
            
            {/* Executions Table */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Execution ID
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Automation
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Contact
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Status
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Started
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {executions.length > 0 ? (
                      executions.map((execution) => (
                        <tr key={execution.id} className={`transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {execution.id.substring(0, 8)}...
                          </td>
                          <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <div className="text-sm">{execution.automationName || 'Unknown'}</div>
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {execution.userEmail}
                            </div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {execution.contactEmail || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(execution.status)}`}>
                              {execution.status}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatDate(execution.startedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {execution.status === 'running' && (
                              <button
                                onClick={() => handleCancelExecution(execution.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                title="Cancel Execution"
                              >
                                ⏹️ Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className={`px-6 py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <div className="text-5xl mb-4">▶️</div>
                          <div>No executions found</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {/* Analytics Tab */}
        {activeTab === 'analytics' && stats && (
          <div className="space-y-6">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} p-6`}>
              <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                System Performance Metrics
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Completion Rate
                  </h3>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {stats.averageCompletionRate.toFixed(1)}%
                  </div>
                  <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${stats.averageCompletionRate}%` }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Avg Execution Time
                  </h3>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {stats.averageExecutionTime.toFixed(0)}m
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
                    Minutes per automation
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total Communications
                  </h3>
                  <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {(stats.totalEmailsSent + stats.totalSmsSent + stats.totalPushSent).toLocaleString()}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
                    Emails • SMS • Push
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Automation Distribution
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Active', value: stats.activeAutomations, color: 'bg-green-600' },
                    { label: 'Paused', value: stats.pausedAutomations, color: 'bg-yellow-600' },
                    { label: 'Draft', value: stats.draftAutomations, color: 'bg-gray-600' },
                    { label: 'Archived', value: stats.archivedAutomations, color: 'bg-gray-400' }
                  ].map(item => {
                    const percentage = stats.totalAutomations > 0 
                      ? (item.value / stats.totalAutomations) * 100 
                      : 0;
                    
                    return (
                      <div key={item.label}>
                        <div className="flex justify-between mb-1">
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{item.label}</span>
                          <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {item.value} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className={`${item.color} h-2 rounded-full`} 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'} rounded-xl shadow-sm ${isDark ? '' : 'shadow-md'} p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Execution Status
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Running', value: stats.runningExecutions, color: 'bg-blue-600' },
                    { label: 'Completed', value: stats.completedExecutions, color: 'bg-green-600' },
                    { label: 'Failed', value: stats.failedExecutions, color: 'bg-red-600' }
                  ].map(item => {
                    const percentage = stats.totalExecutions > 0 
                      ? (item.value / stats.totalExecutions) * 100 
                      : 0;
                    
                    return (
                      <div key={item.label}>
                        <div className="flex justify-between mb-1">
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{item.label}</span>
                          <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {item.value} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className={`${item.color} h-2 rounded-full`} 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => !confirmModal.isLoading && setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default AdminAutomationControl;

// Ensure this file is treated as a module
export {};

